import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { useToast } from '../../hooks/use-toast';
import { 
  Plus, 
  MessageSquare, 
  FileText, 
  LogOut, 
  Crown, 
  Scale,
  Trash2,
  Edit3,
  Vault,
  Shield,
  Users,
  Check,
  X,
  User,
  CreditCard,
  BookOpen,
  Lock,
  Globe,
  BarChart3,
  LifeBuoy,
  MoreVertical,
  Share2,
  Mail,
  Brain,
  FileSignature,
  Home
} from 'lucide-react';
import axios from 'axios';

const Sidebar = ({ onClose }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null); // Track which menu is open
  const [showUpgradeModal, setShowUpgradeModal] = useState(false); // Upgrade modal state
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const API_BASE = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConversations();
    
    // Refresh conversations every 5 seconds to catch new messages/updates
    const interval = setInterval(() => {
      fetchConversations();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [location.pathname]); // Also refresh when navigating to different conversations

  const fetchConversations = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/conversations`);
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async () => {
    try {
      // First, check usage limits before creating
      const checkResponse = await axios.get(`${API_BASE}/api/user/usage`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const { current_usage, plan_limits, subscription_plan } = checkResponse.data;
      const currentMatters = current_usage?.legal_matters_count || 0;
      const limit = plan_limits?.legal_matters || -1;
      
      // Check if user is on free plan and has reached limit
      const isFreeUser = subscription_plan === 'free' || subscription_plan === 'pro_se_starter';
      
      if (isFreeUser && currentMatters >= 1) {
        // Show upgrade modal instead of creating
        setShowUpgradeModal(true);
        return;
      }
      
      // Create conversation if within limits
      const response = await axios.post(`${API_BASE}/api/conversations`, {
        title: 'New Case Matter'
      });
      
      const newConversation = response.data;
      setConversations(prev => [newConversation, ...prev]);
      navigate(`/dashboard/chat/${newConversation.id}`);
      onClose?.();
    } catch (error) {
      console.error('Failed to create conversation:', error);
      
      // Check if error is due to usage limit
      if (error.response?.status === 403 || error.response?.data?.detail?.includes('limit')) {
        setShowUpgradeModal(true);
      } else {
        toast({
          title: "Error",
          description: "Failed to create new case matter. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const deleteConversation = async (conversationId, event) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this conversation? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await axios.delete(`${API_BASE}/api/conversations/${conversationId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      console.log('Delete response:', response.data);
      
      // Remove from local state
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));
      
      // Navigate to dashboard if current conversation is deleted
      if (location.pathname.includes(conversationId)) {
        navigate('/dashboard');
      }
      
      toast({
        title: "Conversation deleted",
        description: "The conversation has been permanently deleted",
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      console.error('Error details:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to delete conversation. Please try again.",
        variant: "destructive"
      });
    }
  };

  const startEditingTitle = (conversation, event) => {
    event.stopPropagation();
    setEditingConversationId(conversation.id);
    setEditingTitle(conversation.title);
  };

  const cancelEditingTitle = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const saveConversationTitle = async (conversationId) => {
    if (!editingTitle.trim()) {
      toast({
        title: "Invalid Title",
        description: "Title cannot be empty",
        variant: "destructive"
      });
      return;
    }

    try {
      const response = await axios.put(
        `${API_BASE}/api/conversations/${conversationId}`,
        { title: editingTitle.trim() },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Update local state
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, title: editingTitle.trim() }
            : conv
        )
      );

      setEditingConversationId(null);
      setEditingTitle('');

      toast({
        title: "Title Updated",
        description: "Conversation title has been updated successfully"
      });
    } catch (error) {
      console.error('Error updating conversation title:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update conversation title. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Toggle menu open/close
  const toggleMenu = (conversationId, event) => {
    event.stopPropagation();
    setOpenMenuId(openMenuId === conversationId ? null : conversationId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openMenuId) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openMenuId]);

  // Share conversation
  const shareConversation = async (conversationId, event) => {
    event.stopPropagation();
    setOpenMenuId(null);
    
    // Generate shareable link
    const shareUrl = `${window.location.origin}/shared/${conversationId}`;
    
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({
        title: "Link Copied",
        description: "Shareable link has been copied to clipboard",
      });
    } catch (error) {
      toast({
        title: "Share Link",
        description: shareUrl,
      });
    }
  };

  // Check if user has admin access
  const isAdmin = user?.email?.includes('admin') || user?.subscription_plan === 'defender_of_the_people' || user?.subscription_plan === 'lifetime' || user?.role === 'admin';
  
  // Check if user has premium access for Sovereign Toolbox
  const hasPremiumAccess = user?.subscription_plan === 'sovereign_shield' || 
                          user?.subscription_plan === 'defender_of_the_people' || 
                          user?.subscription_plan === 'justice_seeker' ||
                          user?.subscription_plan === 'lifetime' ||
                          user?.role === 'admin';

  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'free': return 'bg-gray-100 text-gray-700';
      case 'standard': return 'bg-yellow-100 text-yellow-700';
      case 'premium': return 'bg-red-100 text-red-700';
      case 'lifetime': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPlanIcon = (plan) => {
    if (plan === 'premium' || plan === 'lifetime') {
      return <Crown className="w-3 h-3" />;
    }
    return null;
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="p-3 md:p-4 border-b border-gray-200">
        <div 
          className="flex items-center space-x-2 md:space-x-3 mb-3 md:mb-4 cursor-pointer hover:opacity-80 transition-opacity duration-200"
          onClick={() => navigate('/')}
        >
          <img 
            src="/equalizer-logo.png" 
            alt="TheEqualizer.AI Logo" 
            className="h-8 md:h-10 w-auto object-contain"
          />
          <span className="font-semibold text-gray-900 text-base md:text-lg">TheEqualizer.AI</span>
        </div>
        
        <Button 
          onClick={createNewConversation}
          className="w-full justify-start bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Case Matter
        </Button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full px-2">
          <div className="py-2 space-y-1">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
              </div>
            ) : conversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No conversations yet</p>
                <p className="text-xs">Start a new case matter to begin</p>
              </div>
            ) : (
              conversations.map((conversation) => (
                <div
                  key={conversation.id}
                  className={`
                    relative group rounded-lg transition-colors
                    ${location.pathname.includes(conversation.id) ? 'bg-gray-100' : 'hover:bg-gray-50'}
                  `}
                >
                  {editingConversationId === conversation.id ? (
                    // Editing Mode
                    <div className="flex items-center gap-2 p-3">
                      <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <Input
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        className="text-sm flex-1 h-8"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            saveConversationTitle(conversation.id);
                          } else if (e.key === 'Escape') {
                            cancelEditingTitle();
                          }
                        }}
                        autoFocus
                      />
                      <button
                        onClick={() => saveConversationTitle(conversation.id)}
                        className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 rounded"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEditingTitle}
                        className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    // Display Mode - Three-Dot Menu on LEFT side
                    <div className="flex items-center gap-1.5 md:gap-2 p-2 md:p-3">
                      {/* Three-Dot Menu Button - LEFT SIDE - Always Visible */}
                      <div className="relative flex-shrink-0">
                        <button
                          onClick={(e) => toggleMenu(conversation.id, e)}
                          className="p-1.5 rounded-md hover:bg-gray-300 text-gray-700 hover:text-gray-900 transition-all duration-200"
                          title="More options"
                          type="button"
                        >
                          <MoreVertical className="w-4 h-4 stroke-2" />
                        </button>

                        {/* Dropdown Menu */}
                        {openMenuId === conversation.id && (
                          <div 
                            className="absolute left-0 top-8 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {/* Share Option */}
                            <button
                              onClick={(e) => shareConversation(conversation.id, e)}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left"
                            >
                              <Share2 className="w-4 h-4" />
                              <span>Share</span>
                            </button>

                            {/* Rename Option */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                startEditingTitle(conversation, e);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors text-left"
                            >
                              <Edit3 className="w-4 h-4" />
                              <span>Rename</span>
                            </button>

                            {/* Delete Option */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId(null);
                                deleteConversation(conversation.id, e);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors text-left"
                            >
                              <Trash2 className="w-4 h-4" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      {/* Conversation Content - Clickable */}
                      <div 
                        className="flex-1 min-w-0 flex items-center gap-2 cursor-pointer"
                        onClick={() => {
                          navigate(`/dashboard/chat/${conversation.id}`);
                          onClose?.();
                        }}
                      >
                        <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm font-normal text-gray-900 truncate">
                          {conversation.title}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Footer */}
      <div className="p-3 md:p-4 border-t border-gray-200 space-y-2 md:space-y-3">
        {/* User Plan */}
        <div className="flex items-center justify-between">
          <span className="text-xs md:text-sm text-gray-600">Current Plan</span>
          <Badge 
            variant="outline" 
            className={`text-xs ${getPlanBadgeColor(user?.subscription_plan)}`}
          >
            {getPlanIcon(user?.subscription_plan)}
            <span className={getPlanIcon(user?.subscription_plan) ? 'ml-1' : ''}>
              {user?.subscription_plan?.charAt(0).toUpperCase() + user?.subscription_plan?.slice(1)}
            </span>
          </Badge>
        </div>

        {/* Navigation Links */}
        <div className="space-y-0.5 md:space-y-1 [&_button]:h-9 [&_button]:md:h-10">
          {/* Sovereign Toolbox - Premium Access */}
          {hasPremiumAccess ? (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              onClick={() => {
                navigate('/dashboard/sovereign-toolbox');
                onClose?.();
              }}
            >
              <Crown className="w-4 h-4 mr-2" />
              Sovereign Toolbox
              <Badge className="ml-auto bg-purple-100 text-purple-700 text-xs px-2 py-0.5">
                Premium
              </Badge>
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-gray-400 cursor-not-allowed"
              disabled
            >
              <Lock className="w-4 h-4 mr-2" />
              Sovereign Toolbox
              <Badge className="ml-auto bg-gray-100 text-gray-500 text-xs px-2 py-0.5">
                Locked
              </Badge>
            </Button>
          )}

          {/* Legal Simulations - Premium Access */}
          {hasPremiumAccess ? (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-purple-600 hover:text-purple-700 hover:bg-purple-50"
              onClick={() => {
                navigate('/dashboard/legal-simulations');
                onClose?.();
              }}
            >
              <Brain className="w-4 h-4 mr-2" />
              Legal Simulations
              <Badge className="ml-auto bg-purple-100 text-purple-700 text-xs px-2 py-0.5">
                New
              </Badge>
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-gray-400 cursor-not-allowed"
              disabled
            >
              <Lock className="w-4 h-4 mr-2" />
              Legal Simulations
              <Badge className="ml-auto bg-gray-100 text-gray-500 text-xs px-2 py-0.5">
                Locked
              </Badge>
            </Button>
          )}

          {/* AI Contract Portal - Premium Access */}
          {/* Redress Portal - Premium Access */}
          {/* Foreclosure Defender - Premium Access */}
          {/* === Combined into Remedy AI Agents === */}
          {hasPremiumAccess ? (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
              onClick={() => {
                navigate('/dashboard/remedy-agents');
                onClose?.();
              }}
              data-testid="sidebar-remedy-agents"
            >
              <Scale className="w-4 h-4 mr-2" />
              Remedy AI Agents
              <Badge className="ml-auto bg-indigo-100 text-indigo-700 text-xs px-2 py-0.5">
                New
              </Badge>
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="w-full justify-start text-sm text-gray-400 cursor-not-allowed"
              disabled
            >
              <Lock className="w-4 h-4 mr-2" />
              Remedy AI Agents
              <Badge className="ml-auto bg-gray-100 text-gray-500 text-xs px-2 py-0.5">
                Locked
              </Badge>
            </Button>
          )}

          {/* Smart Response Detection - moved to case view tabs */}

          <Button
            variant="ghost"
            className="w-full justify-start text-sm"
            onClick={() => {
              navigate('/dashboard/account');
              onClose?.();
            }}
          >
            <User className="w-4 h-4 mr-2" />
            Account Settings
          </Button>
          
          {isAdmin && (
            <>
              <Button
                variant="ghost"
                className="w-full justify-start text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                onClick={() => {
                  navigate('/dashboard/admin-portal');
                  onClose?.();
                }}
              >
                <Shield className="w-4 h-4 mr-2" />
                Admin Portal
              </Button>
            </>
          )}
        </div>

        <Separator />

        {/* User Info & Logout */}
        <div className="space-y-2">
          <div className="text-xs text-gray-500 truncate">
            {user?.email}
          </div>
          <Button
            variant="ghost"
            className="w-full justify-start text-sm text-red-600 hover:text-red-700"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </div>
      
      {/* Upgrade Modal for Free Users */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white p-6 rounded-t-lg">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-2xl font-bold mb-2">🔒 Upgrade Required</h2>
                  <p className="text-blue-100">You've reached your limit of 1 free case matter</p>
                </div>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="text-white hover:text-gray-200 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-6">
              <p className="text-gray-700 mb-6 text-lg">
                To create more legal matters and unlock full access to TheEqualizer.AI, please upgrade your subscription.
              </p>
              
              {/* Subscription Options */}
              <div className="space-y-4 mb-6">
                {/* Justice Seeker */}
                <div className="border-2 border-green-500 rounded-lg p-4 hover:shadow-lg transition cursor-pointer"
                     onClick={() => { navigate('/dashboard/subscription'); setShowUpgradeModal(false); }}>
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-xl font-bold text-gray-900">⚖️ Justice Seeker</h3>
                    <span className="text-2xl font-bold text-green-600">$9.99<span className="text-sm">/mo</span></span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>✓ 5 Legal Matters per month</li>
                    <li>✓ Document Templates</li>
                    <li>✓ Priority Email Support</li>
                  </ul>
                </div>
                
                {/* Sovereign Shield */}
                <div className="border-2 border-blue-500 rounded-lg p-4 hover:shadow-lg transition cursor-pointer bg-blue-50"
                     onClick={() => { navigate('/dashboard/subscription'); setShowUpgradeModal(false); }}>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">🛡️ Sovereign Shield</h3>
                      <span className="text-xs bg-blue-600 text-white px-2 py-1 rounded">MOST POPULAR</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">$99<span className="text-sm">/mo</span></span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>✓ Unlimited Legal Matters</li>
                    <li>✓ Advanced AI Analysis</li>
                    <li>✓ Priority Support + Expert Guidance</li>
                    <li>✓ Multi-Country Templates</li>
                  </ul>
                </div>
                
                {/* Defender of the People */}
                <div className="border-2 border-purple-500 rounded-lg p-4 hover:shadow-lg transition cursor-pointer"
                     onClick={() => { navigate('/dashboard/subscription'); setShowUpgradeModal(false); }}>
                  <div className="flex justify-between items-center mb-2">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">👑 Defender of the People</h3>
                      <span className="text-xs bg-purple-600 text-white px-2 py-1 rounded">LIFETIME ACCESS</span>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">$1,497<span className="text-sm"> once</span></span>
                  </div>
                  <ul className="text-sm text-gray-600 space-y-1 ml-4">
                    <li>✓ Everything in Sovereign Shield</li>
                    <li>✓ Lifetime Access (Pay Once)</li>
                    <li>✓ Private Community Forum</li>
                    <li>✓ Exclusive Educational Content</li>
                    <li>✓ Early Access to New Features</li>
                  </ul>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => { navigate('/dashboard/subscription'); setShowUpgradeModal(false); }}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition"
                >
                  View All Plans
                </button>
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-6 py-3 border-2 border-gray-300 rounded-lg font-semibold text-gray-700 hover:bg-gray-50 transition"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Sidebar;